/**
 * Configuración global para modales y diálogos
 *
 * Este archivo centraliza todos los estilos y configuraciones de modales
 * para asegurar consistencia en toda la aplicación.
 */

import { colors } from './colors';
import { spacing } from './spacing';

export interface ModalSize {
  maxWidth: string | false;
  width: string;
  height?: string;
}

export interface ModalHeaderStyle {
  padding: {
    vertical: string;
    horizontal: string;
  };
  background: string;
  color: string;
  borderRadius?: {
    top: string;
    bottom: string;
  };
  borderBottom?: string;
  typography: {
    variant: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    fontSize: string;
    fontWeight: number;
  };
}

export interface ModalContentStyle {
  padding: string;
  backgroundColor?: string;
}

export interface ModalActionsStyle {
  padding: string;
  backgroundColor: string;
  borderTop: string;
  gap: string;
}

export const modalConfig = {
  // Tamaños estándar de modales
  sizes: {
    xs: {
      maxWidth: '444px',
      width: '380px',
    } as ModalSize,
    sm: {
      maxWidth: '600px',
      width: '500px',
    } as ModalSize,
    md: {
      maxWidth: '900px',
      width: '800px',
    } as ModalSize,
    lg: {
      maxWidth: '1200px',
      width: '1000px',
    } as ModalSize,
    xl: {
      maxWidth: '1536px',
      width: '1400px',
    } as ModalSize,
    full: {
      maxWidth: false,
      width: '90vw',
      height: '90vh',
    } as ModalSize,
  },

  // Estilos de header predefinidos
  headers: {
    // Header con gradiente (estilo principal)
    default: {
      padding: {
        vertical: spacing.lg + 'px',
        horizontal: spacing.xl + 'px',
      },
      background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.success.main} 100%)`,
      color: colors.background.paper,
      borderRadius: {
        top: spacing.md + 'px',
        bottom: '0',
      },
      typography: {
        variant: 'h6' as const,
        fontSize: '1.125rem',
        fontWeight: 600,
      },
    } as ModalHeaderStyle,

    // Header minimalista (sin gradiente)
    minimal: {
      padding: {
        vertical: spacing.md + 'px',
        horizontal: spacing.lg + 'px',
      },
      background: colors.background.default,
      color: colors.text.primary,
      borderBottom: `1px solid ${colors.grey[200]}`,
      typography: {
        variant: 'h6' as const,
        fontSize: '1rem',
        fontWeight: 600,
      },
    } as ModalHeaderStyle,

    // Header con gradiente alternativo
    gradient: {
      padding: {
        vertical: spacing.lg + 'px',
        horizontal: spacing.xl + 'px',
      },
      background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.success.main} 100%)`,
      color: colors.background.paper,
      typography: {
        variant: 'h6' as const,
        fontSize: '1.125rem',
        fontWeight: 600,
      },
    } as ModalHeaderStyle,
  },

  // Estilos de contenido
  content: {
    default: {
      padding: `${spacing.lg}px ${spacing.xl}px`,
    } as ModalContentStyle,
    compact: {
      padding: `${spacing.md}px ${spacing.lg}px`,
    } as ModalContentStyle,
    spacious: {
      padding: `${spacing.xl}px ${spacing['2xl']}px`,
    } as ModalContentStyle,
  },

  // Estilos de footer/actions
  actions: {
    default: {
      padding: `${spacing.md}px ${spacing.xl}px`,
      backgroundColor: colors.grey[50],
      borderTop: `1px solid ${colors.grey[200]}`,
      gap: spacing.sm + 'px',
    } as ModalActionsStyle,
    minimal: {
      padding: `${spacing.sm}px ${spacing.lg}px`,
      backgroundColor: 'transparent',
      borderTop: 'none',
      gap: spacing.xs + 'px',
    } as ModalActionsStyle,
  },

  // Border radius estándar
  borderRadius: {
    small: spacing.sm + 'px',
    medium: spacing.md + 'px',
    large: spacing.lg + 'px',
  },

  // Sombras estándar
  shadows: {
    small: '0 2px 8px rgba(0, 0, 0, 0.1)',
    medium: '0 4px 16px rgba(0, 0, 0, 0.12)',
    large: '0 20px 60px rgba(0, 0, 0, 0.18)',
  },

  // Backdrop
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
} as const;

// Variantes predefinidas por tipo de modal
export const modalVariants = {
  confirmation: {
    size: 'xs' as const,
    headerStyle: 'minimal' as const,
    contentStyle: 'compact' as const,
    actionsStyle: 'minimal' as const,
  },
  detail: {
    size: 'md' as const,
    headerStyle: 'gradient' as const,
    contentStyle: 'default' as const,
    actionsStyle: 'default' as const,
  },
  form: {
    size: 'lg' as const,
    headerStyle: 'default' as const,
    contentStyle: 'spacious' as const,
    actionsStyle: 'default' as const,
  },
  info: {
    size: 'sm' as const,
    headerStyle: 'minimal' as const,
    contentStyle: 'compact' as const,
    actionsStyle: 'minimal' as const,
  },
} as const;

export type ModalSizeKey = keyof typeof modalConfig.sizes;
export type ModalHeaderStyleKey = keyof typeof modalConfig.headers;
export type ModalContentStyleKey = keyof typeof modalConfig.content;
export type ModalActionsStyleKey = keyof typeof modalConfig.actions;
export type ModalVariantKey = keyof typeof modalVariants;
